import {
    Component,
    OnInit,
    ViewEncapsulation,
    ViewChild, AfterViewInit 
} from "@angular/core";
import { EventType, Router } from "@angular/router";
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    Validators,
} from "@angular/forms";
import * as strings from "../stringFiles/academicCalendar.json";
import { firstValueFrom } from "rxjs";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { CalendarOptions } from "@fullcalendar/core/index.js";
import { AcademicCalendarService } from "./academic-calendar.service";
import { AcademicHolidaysService } from "../academic-holidays/academic-holidays.service";
import { EventCategoryService } from "../event-category/event-category.service";
import timeGridPlugin from '@fullcalendar/timegrid';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { TranslateService } from "@ngx-translate/core";
import {  CalendarEvent, CalendarLocale, CalendarView } from 'ng-full-calendar';


declare var $: any;
declare var customToastr: any;
@Component({
    selector: "app-academic-calendar",
    templateUrl: "./academic-calendar.component.html",
    styleUrls: [
        "./academic-calendar.component.css",
        "./../../assets/css/theme-default/bootstrap.css",
        "./../../assets/css/theme-default/materialadmin.css",
        "./../../assets/css/theme-default/font-awesome.min.css",
        "./../../assets/css/theme-default/material-design-iconic-font.min.css",
        "./../../assets/css/theme-default/libs/DataTables/jquery.dataTables.css",
        "./../../assets/css/theme-default/libs/bootstrap-datepicker/datepicker3.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/dataTables.colVis.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/buttons.dataTables.min.css",
        "./../../assets/css/theme-default/libs/DataTables/extensions/dataTables.tableTools.css",
        "./../../_crop/css/style.css",
        "./../../_crop/css/style-example.css",
    ],
    encapsulation: ViewEncapsulation.None,
})
export class AcademicCalendarComponent implements OnInit {
    @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
    language = "en";
    strings = strings;
    staffList = [];
    validDateRange: { start: string; end: string } = { start: '', end: '' };
    calendarOptions: CalendarOptions;
    ngfcLocale: CalendarLocale | null = null;
    academicStartDate: string = '';
    academicEndDate: string = '';
    currentStartDate: string = '';
    currentEndDate: string = '';
    academicCalendarForm: FormGroup;
    academicCalendarFormSubmit = false;
    showOverlay = false;
    createEventVisible = false;
    editEventVisible = false;
    deleteEventVisible = false;
    eventDetailsVisible = false;
    classDropdownDataArray: any[] = [];
    groupedClassDropdownData: any[] = [];
    selectedClassId: any[] = [];
    selectedStaffId: any[] = [];
    staffListData: any[] = [];
    eventDetails: any[] = [];
    calendarEvents = [];
    fullClassData = [];
    eventCategoryListData: any[] = [];
    holidayDates: string[] = [];
    isCalendarPublish = false;
    showPublishButton = false;
    editButtonVisible = false;
    editMode = false;
    selectedEventId = '';
    academicCalendarId = null;
    calendarPublishConfirmBox = false;
    isPageLoad = false;

    // function for generate dummy array for skeleton loader
    generateDummyArray(count: number): any[] {
        return Array.from({ length: count }, () => ({}));
    }
    selectedView: string = 'dayGridMonth';
    selectedEventType : string = ''
    eventRepeatId  = null
    eventPopupVisible = false;
    categoryColorList: any[] = [];
    showAddCategoryInput = false;
    newCategoryName = '';
    newCategoryColorId: number | null = null;
    creatingCategory = false;
    editingCategoryId: number | null = null;
    deleteCategoryConfirmVisible = false;
    deletingCategoryId: number | null = null;
    deletingCategory: any = null;
    deletingCategorySubmit = false;
    categoryDeleteConflictData: any[] = [];
    categoryDeleteConfirmRequired = false;

    get deletingCategoryColor(): string | null {
        const color = this.categoryColorList.find((c: any) => c.pkColorId === this.deletingCategory?.pkColorId);
        return color?.colorCode || null;
    }

    // "2026-27"-style label for the academic year currently being published
    get academicYearLabel(): string {
        if (!this.academicStartDate || !this.academicEndDate) {
            return '';
        }
        const startYear = new Date(this.academicStartDate).getFullYear();
        const endYear = new Date(this.academicEndDate).getFullYear();
        return `${startYear}-${endYear.toString().slice(-2)}`;
    }

    // resolve the hex color dot for any event category row (dropdown list + selected value)
    getCategoryColor(category: any): string | null {
        const color = this.categoryColorList.find((c: any) => c.pkColorId === category?.pkColorId);
        return color?.colorCode || null;
    }

    // stable (non-getter) category list for the ngfc-calendar sidebar filter, so every category shows up even with zero events.
    // Must stay a plain property, not a getter: ngfc-calendar resets its own toggle state (activeCategoryIds) whenever this
    // input's array reference changes, so a getter (new array every change-detection cycle) would silently undo any filter toggle.
    ngfcCategoriesList: { id: string; label: string; color: string }[] = [];

    // ngfc-calendar manages its own navigation internally and does not auto-jump to newly created events,
    // so this tracks/drives its [(date)] to bring a just-created event/holiday's month into view.
    ngfcDate: Date = new Date();

    // localStorage key + valid values for remembering the user's last-used calendar view (month/week/day/agenda)
    private readonly academicCalendarModeStorageKey = 'academicCalendarMode';
    private readonly validNgfcViews: CalendarView[] = ['month', 'week', 'day', 'agenda'];

    // ngfc-calendar's [(view)], initialized from localStorage if a valid mode was saved, else defaults to month
    ngfcView: CalendarView = this.getStoredCalendarMode();

    getStoredCalendarMode(): CalendarView {
        const storedMode = localStorage.getItem(this.academicCalendarModeStorageKey) as CalendarView;
        return this.validNgfcViews.includes(storedMode) ? storedMode : 'month';
    }

    // function to persist the calendar view whenever the user switches it, so it's restored on next visit
    onNgfcViewChange(view: CalendarView) {
        this.ngfcView = view;
        localStorage.setItem(this.academicCalendarModeStorageKey, view);
    }

    // synthetic category id for holiday events, since the backend does not reserve any particular
    // fkEventCategory value for holidays (isHoliday is the actual source of truth).
    readonly holidayCategoryId = 'holiday';

    // function to rebuild ngfcCategoriesList; call after eventCategoryListData or categoryColorList changes
    refreshNgfcCategories() {
        this.ngfcCategoriesList = this.eventCategoryListData.map((category: any) => {
            const resolvedColor = this.categoryColorList.find((c: any) => c.pkColorId === category.pkColorId);
            return {
                id: String(category.id),
                label: category.displayName,
                color: this.mapHexToNearestNamedColor(resolvedColor?.colorCode),
            };
        });
    }

    // helper to map an arbitrary hex color to the closest of ngfc-calendar's 8 supported named colors,
    // since its sidebar checkbox only recognizes those fixed CSS classes (ngfc-color-<name>)
    mapHexToNearestNamedColor(hex: string | undefined): string {
        const namedColors: { name: string; rgb: [number, number, number] }[] = [
            { name: 'blue', rgb: [76, 141, 245] },
            { name: 'green', rgb: [90, 166, 107] },
            { name: 'red', rgb: [224, 113, 95] },
            { name: 'yellow', rgb: [219, 169, 58] },
            { name: 'purple', rgb: [151, 114, 214] },
            { name: 'orange', rgb: [224, 138, 62] },
            { name: 'teal', rgb: [75, 166, 156] },
            { name: 'gray', rgb: [139, 147, 161] },
        ];
        if (!hex) {
            return 'gray';
        }
        const normalizedHex = hex.replace('#', '');
        if (normalizedHex.length !== 6) {
            return 'gray';
        }
        const r = parseInt(normalizedHex.substring(0, 2), 16);
        const g = parseInt(normalizedHex.substring(2, 4), 16);
        const b = parseInt(normalizedHex.substring(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            return 'gray';
        }
        let closestColor = 'gray';
        let smallestDistance = Infinity;
        for (const namedColor of namedColors) {
            const distance = Math.pow(r - namedColor.rgb[0], 2) +
                Math.pow(g - namedColor.rgb[1], 2) +
                Math.pow(b - namedColor.rgb[2], 2);
            if (distance < smallestDistance) {
                smallestDistance = distance;
                closestColor = namedColor.name;
            }
        }
        return closestColor;
    }

    editHolidayPopupVisible = false;
    editHolidayForm: FormGroup;
    editHolidayFormSubmit = false;
    selectedHolidayEventId: any = '';
    selectedHolidayCalendarEventId: any = '';
    selectedHolidayClassId: any[] = [];
    deleteHolidayConfirmVisible = false;
    deletingHolidaySubmit = false;
    createChooserPopupVisible = false;
    createChooserSelection: 'event' | 'holiday' | null = null;
    createChooserSelectedDate: Date | null = null;
    createHolidayPopupVisible = false;
    createHolidayForm: FormGroup;
    createHolidayFormSubmit = false;
    createHolidaySelectedClassId: any[] = [];

    get createHolidayDurationDays(): number {
        const start = this.createHolidayForm?.get('holidayStartDate')?.value;
        const end = this.createHolidayForm?.get('holidayEndDate')?.value;
        if (!start || !end) return 0;
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffMs = endDate.getTime() - startDate.getTime();
        return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
    }

    get editHolidayDurationDays(): number {
        const start = this.editHolidayForm?.get('holidayStartDate')?.value;
        const end = this.editHolidayForm?.get('holidayEndDate')?.value;
        if (!start || !end) return 0;
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffMs = endDate.getTime() - startDate.getTime();
        return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
    }
    events: CalendarEvent[] = [];
    constructor(
        private academicEventCalendarService: AcademicCalendarService,
        private academicHolidaysService: AcademicHolidaysService,
        private eventCategoryService: EventCategoryService,
        private router: Router,
        private fb: FormBuilder,
        private translate: TranslateService
    ) {
        this.academicCalendarForm = this.fb.group({
            academicEventName: [
                { value: "", disabled: false },
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(200),
                    // this.alphaNumericWithSpacesValidator
                ],
            ],
            startDate: [
                { value: "", disabled: false },
                [Validators.required]
            ],
            endDate: [
                { value: "", disabled: false },
                []
            ],
            startTime: [
                { value: '', disabled: false },
                []
            ],
            endTime: [
                { value: "", disabled: false },
                []
            ],
            academicEventDescription: [
                { value: "", disabled: false },
                [
                    Validators.minLength(5),
                    Validators.maxLength(500),
                    this.checkSpaceInString
                ]
            ],
            selectedClasses: [
                { value: [], disabled: false },
                [Validators.required]
            ],
            selectedStaffs: [
                { value: [], disabled: false },
                [Validators.required]
            ],
            selectedEventCategory: [
                { value: null, disabled: false },
                [Validators.required]
            ],
            selectedEventType: [
                { value: '', disabled: false },
                [Validators.required]
            ]
        })

        this.editHolidayForm = this.fb.group({
            holidayName: [
                "",
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(60)
                ]
            ],
            holidayDescription: [
                "",
                [
                    Validators.minLength(5),
                    Validators.maxLength(500),
                    this.checkSpaceInString
                ]
            ],
            holidayStartDate: [
                "",
                [Validators.required]
            ],
            holidayEndDate: [
                "",
                [Validators.required]
            ],
            holidaySelectedClasses: [
                [],
                [Validators.required]
            ]
        })

        this.createHolidayForm = this.fb.group({
            holidayName: [
                "",
                [
                    Validators.required,
                    Validators.minLength(3),
                    Validators.maxLength(60)
                ]
            ],
            holidayDescription: [
                "",
                [
                    Validators.minLength(5),
                    Validators.maxLength(500),
                    this.checkSpaceInString
                ]
            ],
            holidayStartDate: [
                "",
                [Validators.required]
            ],
            holidayEndDate: [
                "",
                [Validators.required]
            ],
            holidaySelectedClasses: [
                [],
                [Validators.required]
            ]
        })
    }

     getSchoolInfo(){
     return  localStorage.getItem("schoolAllInfo")
    }

    ngOnInit() {
        const schoolInfo = this.getSchoolInfo();
        const parsedInfo = JSON.parse(schoolInfo?schoolInfo :"")
        this.getEventList(parsedInfo.currentAcademicStartDate,parsedInfo.currentAcademicEndDate)
        this.featureCheck();
        // this.loadCalendar();
        this.getStaffList();
        this.getClassList();
        this.getEventCategoryList();
        this.getEventCategoryColorList();
        this.handleEventTypeValidation()

        this.setTranslatedCalendarOptions();

        this.translate.onLangChange.subscribe(() => {
            this.setTranslatedCalendarOptions();
        });
        
    }

    // function to build the ngfc-calendar [locale] input (translated weekday/month names + 12h time) from the app's i18n files
    setTranslatedCalendarOptions() {
        this.translate.get([
            'calendar.months',
            'calendar.monthsShort',
            'calendar.weekdays',
            'calendar.weekdaysShort'
        ]).subscribe(translated => {
            this.ngfcLocale = {
                weekdayNamesShort: translated['calendar.weekdaysShort'],
                weekdayNamesLong: translated['calendar.weekdays'],
                monthNamesShort: translated['calendar.monthsShort'],
                monthNamesLong: translated['calendar.months'],
                timeFormat: '12h',
            };
        });
    }


    // function to check white space in string
    checkSpaceInString(control: AbstractControl) {
        if (control.value) {
            if (control.value.trim() === '') return { invalidCharacter: true }
        }
    }

    handleEventTypeValidation() {
        this.academicCalendarForm.get('selectedEventType')?.valueChanges.subscribe(eventType => {
            const startTime = this.academicCalendarForm.get('startTime');
            const endTime = this.academicCalendarForm.get('endTime');
            const endDate = this.academicCalendarForm.get('endDate');
            if (eventType === 'repeat') {
                startTime?.setValidators([Validators.required]);
                endTime?.setValidators([Validators.required]);
                endDate?.setValidators([Validators.required]); 
            } else if (eventType === 'all' || eventType === 'fullAcademicYear') {
                startTime?.clearValidators();
                endTime?.clearValidators();
                endDate?.clearValidators();
                endDate?.setValue(null);
            } else {
                startTime?.clearValidators();
                endTime?.clearValidators();
                endDate?.setValidators([Validators.required]);
            }
            startTime?.updateValueAndValidity();
            endTime?.updateValueAndValidity();
            endDate?.updateValueAndValidity();
        });
    }
    

    // function for check white spaces and check strings should contains alphabets and numbers 
    alphaNumericWithSpacesValidator(control: AbstractControl): { [key: string]: any } | null {
        const regex = /^[a-zA-Z0-9\s]+$/;
        if (!control.value) {
            return null;
        }
        const value = control.value.trim();
        if (value === '') return { invalidCharacter: true };
        if (value) {
            if (!regex.test(value)) {
                return { invalidCharacter: true };
            }
        }
        return null;
    }

    // function for validates the start and end date
    validateDates(): void {
        const eventType = this.academicCalendarForm.get('selectedEventType')?.value
        const startDateControl = this.academicCalendarForm.get('startDate');
        const endDateControl = this.academicCalendarForm.get('endDate');
        const startTimeControl = this.academicCalendarForm.get('startTime');
        const endTimeControl = this.academicCalendarForm.get('endTime');
        const startTime = this.academicCalendarForm.get('startTime')?.value;
        const endTime = this.academicCalendarForm.get('endTime')?.value
        const startDate = startDateControl?.value ? new Date(startDateControl.value) : null;
        const endDate = endDateControl?.value ? new Date(endDateControl.value) : null;
        const academicStartDate = new Date(this.academicStartDate);
        const academicEndDate = new Date(this.academicEndDate);
        if (!startDate) {
            startDateControl?.setErrors({ required: true });
        }
        else if (startDate && academicStartDate && startDate < academicStartDate) {
            startDateControl?.setErrors({ invalidStartDate: true });
        }
        else {
            startDateControl?.setErrors(null);
        }
        if (!endDate && eventType !==  'fullAcademicYear' ) {
            endDateControl?.setErrors({ required: true })
        }
        else if (endDate && academicEndDate && endDate > academicEndDate && eventType !== 'fullAcademicYear' ) {
            endDateControl?.setErrors({ invalidEndDate: true });
        }
        else {
            endDateControl?.setErrors(null);
        }
        if (startDate && endDate && startDate > endDate && eventType !== 'fullAcademicYear') {
            startDateControl?.setErrors({ startDateMorethenEndDate: true });
        }
        if (startTime && endTime) {
            const sameDay = !startDate || !endDate || startDate.getTime() === endDate.getTime();
            const startTimeDate = new Date(`1970-01-01T${startTime}:00`);
            const endTimeDate = new Date(`${sameDay ? '1970-01-01' : '1970-01-02'}T${endTime}:00`);

            if (endTimeDate <= startTimeDate) {
                startTimeControl?.setErrors({ startTimeMorethenEndTime: true });
                endTimeControl?.setErrors({ invalidEndTime: true });
            } else {
                startTimeControl?.setErrors(null);
                endTimeControl?.setErrors(null);
            }
        } else {
            startTimeControl?.setErrors(null);
            endTimeControl?.setErrors(null);
        }
        this.academicCalendarForm.updateValueAndValidity();
    }

    // function for check academic calendar feature is enabled or not 
    featureCheck() {
        const extensionServices = localStorage.getItem('extensionServices');
        const data = JSON.parse(extensionServices);
        const featureEnabled = data.some(item => item.serviceName === 'ACADEMIC_CALENDAR')
        featureEnabled == true ?
            this.router.navigate(["/academic/calendar"]) : this.router.navigate(["***"])
    }

    // function for add the classes in the array in the dropdown
    selectClasses(selectedIds: any) {
        this.academicCalendarForm.markAsDirty();
        this.selectedClassId = [...new Set(selectedIds || [])];
    }

    // function for select all classes in the dropdown
    selectAllClasses() {
        this.academicCalendarForm.markAsDirty();
        this.selectedClassId = [];
        const allClasses: any = this.classDropdownDataArray
            .filter(item => !item.items)
            .map(item => item.pkClassStandardsId);
        this.selectedClassId = allClasses;
        this.academicCalendarForm.get('selectedClasses').setValue(allClasses);
    }

    // function to check whether every class in a group is currently selected, to drive the group header's checkbox state
    isClassGroupFullySelected(group: any, selectedIds: any[]): boolean {
        const groupIds = (group?.items || []).map((item: any) => item.pkClassStandardsId);
        return groupIds.length > 0 && groupIds.every(id => selectedIds?.includes(id));
    }

    // function to toggle every class in a group on/off together, for a given reactive form + control name
    toggleClassGroup(group: any, form: FormGroup, controlName: string, selectedIds: any[]) {
        const groupIds = (group?.items || []).map((item: any) => item.pkClassStandardsId);
        const isFullySelected = this.isClassGroupFullySelected(group, selectedIds);
        const currentIds: any[] = selectedIds || [];
        const updatedIds = isFullySelected
            ? currentIds.filter(id => !groupIds.includes(id))
            : [...new Set([...currentIds, ...groupIds])];
        form.get(controlName)?.setValue(updatedIds);
        form.markAsDirty();
        return updatedIds;
    }

    // function to toggle a class group for the Event Info popup's class selector
    toggleEventClassGroup(group: any, event: Event) {
        event.stopPropagation();
        const currentIds = this.academicCalendarForm.get('selectedClasses')?.value || [];
        this.selectedClassId = this.toggleClassGroup(group, this.academicCalendarForm, 'selectedClasses', currentIds);
    }

    // function to toggle a class group for the Edit Holiday popup's class selector
    toggleEditHolidayClassGroup(group: any, event: Event) {
        event.stopPropagation();
        const currentIds = this.editHolidayForm.get('holidaySelectedClasses')?.value || [];
        this.selectedHolidayClassId = this.toggleClassGroup(group, this.editHolidayForm, 'holidaySelectedClasses', currentIds);
    }

    // function to toggle a class group for the Add Holiday popup's class selector
    toggleCreateHolidayClassGroup(group: any, event: Event) {
        event.stopPropagation();
        const currentIds = this.createHolidayForm.get('holidaySelectedClasses')?.value || [];
        this.createHolidaySelectedClassId = this.toggleClassGroup(group, this.createHolidayForm, 'holidaySelectedClasses', currentIds);
    }

    // function for add the staff id in the array select staff in the dropdown
    selectStaffs(selectedIds: any) {
        this.academicCalendarForm.markAsDirty();
        this.selectedStaffId = [...new Set(selectedIds || [])];
    }

    // function for select all the staff
    selectAllStaffs() {
        this.academicCalendarForm.markAsDirty();
        this.selectedStaffId = [];
        const allStaffId: any = this.staffListData
            .filter(item => !item.items)
            .map(item => item.staffId);
        this.selectedStaffId = allStaffId;
        this.academicCalendarForm.get('selectedStaffs').setValue(this.selectedStaffId);
    }

    // function for format the data for the dropdown
    convertDataForClassDropdown() {
        this.classDropdownDataArray = [];
        this.fullClassData.forEach(category => {
            category.classStandards.forEach(classes => {
                classes.classes.forEach(standard => {
                    this.classDropdownDataArray.push({
                        classCategoryName: category.classCategoryName,
                        classStandardName: classes.classStandardName,
                        classDivisionName: standard.classDivision,
                        displayName: `${category.classCategoryName} - ${classes.classStandardName} - ${standard.classDivision}`,
                        categoryId: category.categoryId,
                        pkClassStandardsId: standard.classId
                    });
                });
            });
        });
        this.buildGroupedClassDropdownData();
    }

    // function to group the flat class dropdown data by category for the "grouped" multi-select display
    buildGroupedClassDropdownData() {
        const groups: any[] = [];
        this.classDropdownDataArray.forEach(item => {
            let group = groups.find(g => g.classCategoryName === item.classCategoryName);
            if (!group) {
                group = { classCategoryName: item.classCategoryName, items: [] };
                groups.push(group);
            }
            group.items.push(item);
        });
        this.groupedClassDropdownData = groups;
    }

    // function for get the class list details from the api
    async getClassList() {
        try {
            const response: any = await firstValueFrom(this.academicHolidaysService.getClassListService())
            if (response?.status) {
                this.fullClassData = response?.body?.classDetailList;
                this.convertDataForClassDropdown();
            }
        }
        catch (error) {
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error.error.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function for get the event category list
    async getEventCategoryList() {
        try {
            const response: any = await firstValueFrom(this.eventCategoryService.getEventCategory());
            if (response?.status) {
                const eventCategoryList = response?.body?.categoryList;
                this.eventCategoryListData = eventCategoryList.map(item => ({
                    id: item.pkEventCategory,
                    displayName: item.categoryName,
                    pkColorId: item.pkColorId
                }));
                if (!this.eventCategoryListData.some(item => item.id === this.holidayCategoryId)) {
                    this.eventCategoryListData.unshift({
                        id: this.holidayCategoryId,
                        displayName: "Holiday",
                        disabled: true
                    });
                }
                this.refreshNgfcCategories();
            }
        }
        catch (error) {
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error.error.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function for get the color options used when creating a new event category inline
    async getEventCategoryColorList() {
        try {
            const response: any = await firstValueFrom(this.eventCategoryService.getColorListAPI());
            if (response?.status) {
                this.categoryColorList = response?.body?.colorOptions || [];
                this.refreshNgfcCategories();
            }
        }
        catch (error) {
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error.error.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function to reveal the inline "new category" name + color inputs
    openAddCategoryInput() {
        this.editingCategoryId = null;
        this.showAddCategoryInput = true;
        this.newCategoryName = '';
        this.newCategoryColorId = null;
    }

    // function to reveal the inline form pre-filled for editing an existing category
    openEditCategoryInput(category: any, event: Event) {
        event.stopPropagation();
        this.editingCategoryId = category.id;
        this.showAddCategoryInput = true;
        this.newCategoryName = category.displayName;
        this.newCategoryColorId = category.pkColorId || null;
    }

    // function to cancel the inline "new category" creation or edit
    cancelAddCategoryInput() {
        this.showAddCategoryInput = false;
        this.newCategoryName = '';
        this.newCategoryColorId = null;
        this.editingCategoryId = null;
    }

    // function to pick the color for the category being created or edited inline
    selectNewCategoryColor(pkColorId: number) {
        this.newCategoryColorId = pkColorId;
    }

    // function for create or edit an event category inline from the event popup and select it
    async createEventCategoryInline() {
        if (this.editingCategoryId) {
            await this.editEventCategoryInline();
        }
        else {
            await this.addEventCategoryInline();
        }
    }

    // function for create a new event category inline from the event popup and select it
    async addEventCategoryInline() {
        try {
            const categoryName = this.newCategoryName?.trim();
            if (!categoryName || !this.newCategoryColorId) {
                return;
            }
            this.creatingCategory = true;
            const body = {
                lang: this.language,
                categoryName,
                colorCode: this.newCategoryColorId,
                academicId: this.eventCategoryService.academicId
            };
            const response: any = await firstValueFrom(this.eventCategoryService.createEventCategory(body));
            this.creatingCategory = false;
            if (response?.status) {
                await this.getEventCategoryList();
                const createdCategory = this.eventCategoryListData.find(item => item.displayName === categoryName);
                if (createdCategory) {
                    this.academicCalendarForm.get('selectedEventCategory')?.setValue(createdCategory.id);
                    this.academicCalendarForm.markAsDirty();
                }
                this.cancelAddCategoryInput();
            }
            else {
                const CategoryFailedToCreateErrorTextTranslation = await firstValueFrom(this.translate.get('CategoryFailedToCreateErrorText'));
                customToastr(CategoryFailedToCreateErrorTextTranslation, "error");
            }
        }
        catch (error) {
            this.creatingCategory = false;
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error?.error?.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function for edit an existing event category inline from the event popup
    async editEventCategoryInline() {
        try {
            const categoryName = this.newCategoryName?.trim();
            if (!categoryName || !this.newCategoryColorId) {
                return;
            }
            this.creatingCategory = true;
            const body = {
                lang: this.language,
                eventCategoryId: this.editingCategoryId,
                categoryName,
                colorCode: this.newCategoryColorId,
                academicId: this.eventCategoryService.academicId
            };
            const response: any = await firstValueFrom(this.eventCategoryService.editEventCategory(body));
            this.creatingCategory = false;
            if (response?.status) {
                await this.getEventCategoryList();
                this.cancelAddCategoryInput();
            }
            else {
                const CategoryFailedToCreateErrorTextTranslation = await firstValueFrom(this.translate.get('CategoryFailedToCreateErrorText'));
                customToastr(CategoryFailedToCreateErrorTextTranslation, "error");
            }
        }
        catch (error) {
            this.creatingCategory = false;
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error?.error?.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function to open the delete-confirmation popup for a category
    openDeleteCategoryConfirm(category: any, event: Event) {
        event.stopPropagation();
        this.deletingCategoryId = category.id;
        this.deletingCategory = category;
        this.deleteCategoryConfirmVisible = true;
        this.categoryDeleteConfirmRequired = false;
        this.categoryDeleteConflictData = [];
    }

    // function to close the delete-confirmation popup for a category
    closeDeleteCategoryConfirm() {
        this.deleteCategoryConfirmVisible = false;
        this.deletingCategoryId = null;
        this.deletingCategory = null;
        this.categoryDeleteConfirmRequired = false;
        this.categoryDeleteConflictData = [];
    }

    // function for delete an event category, warning first if events already use it
    async deleteEventCategoryInline() {
        try {
            const body = {
                lang: this.language,
                eventCategoryId: this.deletingCategoryId,
                isDeleteConfirmed: null,
                academicId: this.eventCategoryService.academicId
            };
            this.deletingCategorySubmit = true;
            const response: any = await firstValueFrom(this.eventCategoryService.deleteEventCategory(body));
            this.deletingCategorySubmit = false;
            if (response?.status) {
                if (response?.body?.isConfirmationRequired) {
                    this.categoryDeleteConfirmRequired = true;
                    this.categoryDeleteConflictData = response?.body?.isEventScheduledWithCategoryResponse || [];
                }
                else {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, "success");
                    }
                    else {
                        const deletEventCategorySuccessfullTranslation = await firstValueFrom(this.translate.get('deletEventCategorySuccessfull'));
                        customToastr(deletEventCategorySuccessfullTranslation, "success");
                    }
                    await this.getEventCategoryList();
                    this.closeDeleteCategoryConfirm();
                }
            }
        }
        catch (error) {
            this.deletingCategorySubmit = false;
            const deleteEventCategoryErrorTranslation = await firstValueFrom(this.translate.get('deleteEventCategoryError'));
            customToastr(error?.error?.message ? error.error.message : deleteEventCategoryErrorTranslation, "error");
        }
    }

    // function for confirming deletion of a category already used by scheduled events
    async confirmDeleteEventCategoryInline() {
        try {
            const body = {
                lang: this.language,
                eventCategoryId: this.deletingCategoryId,
                isDeleteConfirmed: "yes",
                academicId: this.eventCategoryService.academicId
            };
            this.deletingCategorySubmit = true;
            const response: any = await firstValueFrom(this.eventCategoryService.deleteEventCategory(body));
            this.deletingCategorySubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, "success");
                }
                else {
                    const deletEventCategorySuccessfullTranslation = await firstValueFrom(this.translate.get('deletEventCategorySuccessfull'));
                    customToastr(deletEventCategorySuccessfullTranslation, "success");
                }
                await this.getEventCategoryList();
            }
            else {
                const deleteEventCategoryErrorTranslation = await firstValueFrom(this.translate.get('deleteEventCategoryError'));
                customToastr(deleteEventCategoryErrorTranslation, "error");
            }
            this.closeDeleteCategoryConfirm();
        }
        catch (error) {
            this.deletingCategorySubmit = false;
            const deleteEventCategoryErrorTranslation = await firstValueFrom(this.translate.get('deleteEventCategoryError'));
            customToastr(error?.error?.message ? error.error.message : deleteEventCategoryErrorTranslation, "error");
        }
    }

    // function for get the staff details list for the staff dropdown
    async getStaffList() {
        try {
            const response: any = await firstValueFrom(this.academicEventCalendarService.getStaffListService());
            if (response?.status) {
                this.staffList = response?.body?.staffList;
                this.staffListData = this.staffList.map(staff => ({
                    staffId: staff.staffId,
                    displayName: staff.staffName
                }));
            }
        }
        catch (error) {
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error.error.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // Helper function to generate an array of dates between two dates
    generateDateRange(start: string, end: string): string[] {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const dateArray = [];
        let currentDate = startDate;
        while (currentDate <= endDate) {
            dateArray.push(currentDate.toISOString());
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return dateArray;
    }

    // function for get the event list
    async getEventList(startDate,endDate) {
        try {
            const eventCategoryId = this.strings.allEventCategoryId;
            this.isPageLoad = true;
            const response: any = await firstValueFrom(this.academicEventCalendarService.getEventListService(startDate,endDate,"All"));
            console.log("ressss",response);

            this.isPageLoad = false;
            if (response?.status) {
                 this.academicStartDate = response?.body?.academicStartDate;
                this.academicEndDate = response?.body?.academicEndDate;
                this.isCalendarPublish = response?.body?.isCalendarPublish;
                this.showPublishButton = response?.body?.showPublishButton;
                this.academicCalendarId = response?.body?.academicCalendarId;
                this.validDateRange = { start: this.academicStartDate, end: this.academicEndDate };
                this.eventDetails = response.body.eventList || [];
                this.selectedEventType = response?.body?.eventType
                this.refreshCalendarViewFromEventDetails();
            }
        }
        catch (error) {
            this.isPageLoad = false;
            if (error?.error?.message) {
                customToastr(error?.error?.message, "error");
            }
            else {
                const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
                customToastr(getApiErrorMessageTranslation, "error");
            }
        }
    }

    // function to rebuild the calendar's rendered event data from the in-memory eventDetails array
    refreshCalendarViewFromEventDetails() {
        const formatEventData = this.convertDataToEventData(this.eventDetails);
        this.calendarEvents = [...formatEventData];
        this.events = this.convertDataToNgfcEventData(this.eventDetails);
        this.holidayDates = this.eventDetails
            .filter(event => event.isHoliday)
            .flatMap(event => this.generateDateRange(event.startDate, event.endDate));
        this.holidayDates = [...new Set(this.holidayDates)];
        this.calendarOptions = {
            ...this.calendarOptions,
            validRange: this.validDateRange,
            events: this.calendarEvents
        };
    }

    // function to patch a single event's fields into eventDetails and refresh the calendar view, without re-fetching from the API
    updateLocalEventRecord(eventId: any, updatedFields: any) {
        const index = this.eventDetails.findIndex((item) => item?.pkEventId == eventId);
        if (index === -1) {
            return;
        }
        this.eventDetails[index] = { ...this.eventDetails[index], ...updatedFields };
        this.refreshCalendarViewFromEventDetails();
    }

    // function to remove a single event from eventDetails and refresh the calendar view, without re-fetching from the API
    removeLocalEventRecord(eventId: any) {
        this.eventDetails = this.eventDetails.filter((item) => item?.pkEventId != eventId);
        this.refreshCalendarViewFromEventDetails();
    }

    // helper function to parse the API's "...T23:59:00.000Z"-style datetime string as literal
    // local wall-clock numbers, instead of letting JS treat the trailing Z as a real UTC instant
    // and shift it by the browser's timezone offset when the calendar library reads it back with
    // local-time getters.
    parseApiDate(iso: string): Date {
        const [datePart, timePart] = iso.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = (timePart || '00:00').split(':').map(Number);
        return new Date(year, month - 1, day, hour, minute);
    }

    // helper function to detect whether an event's start/end times represent a full day.
    // Since a `time` input can't select 24:00, a whole-day custom event is saved as
    // 00:00 -> 23:59, not 00:00 -> 00:00, so both must be treated as "all day".
    isFullDayRange(start: Date, end: Date): boolean {
        const startsAtMidnight = start.getHours() === 0 && start.getMinutes() === 0;
        const endsAtMidnight = end.getHours() === 0 && end.getMinutes() === 0;
        const endsAtDayClose = end.getHours() === 23 && end.getMinutes() === 59;
        return startsAtMidnight && (endsAtMidnight || endsAtDayClose);
    }

    // helper function to transform the event list to the ng-full-calendar event data
    convertDataToNgfcEventData(dataList: any): CalendarEvent[] {
        return dataList.map(event => {
            const start = this.parseApiDate(event.startDate);
            const end = event.endDate ? this.parseApiDate(event.endDate) : this.parseApiDate(event.startDate);
            const allDay = this.isFullDayRange(start, end);
            return {
                id: String(event.pkEventId),
                title: event.eventName,
                start,
                end,
                allDay,
                color: event.colorCode,
                description: event.description,
                // holidays get a dedicated synthetic category instead of trusting fkEventCategory to line up
                // with the hardcoded "Holiday" pseudo-category id, since the backend does not reserve any
                // particular fkEventCategory value for holidays.
                calendarId: event.isHoliday ? this.holidayCategoryId : (event.fkEventCategory != null ? String(event.fkEventCategory) : undefined),
                meta: {
                    isHoliday: event.isHoliday,
                    fkEventCategory: event.fkEventCategory,
                    colorName: event.colorName,
                    eventType: event.eventType,
                    eventRepeatId: event.eventRepeatId,
                    classId: event.classId,
                    staffAccessibility: event.staffAccessibility,
                },
            };
        });
    }

    // helper function to format a Date using its own local wall-clock fields as an ISO-shaped
    // string, instead of toISOString() (which converts to a real UTC instant and would shift
    // the date/time by the browser's timezone offset when the consumer reads it back).
    formatLocalIso(date: Date): string {
        const pad = (n: number, len = 2) => n.toString().padStart(len, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T` +
            `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.` +
            `${pad(date.getMilliseconds(), 3)}Z`;
    }

    // helper function to transform the event list to the full calendar event data
    convertDataToEventData(dataList: any) {
        return dataList.map((event: any) => {
            let start = this.parseApiDate(event.startDate);
            let end = event.endDate ? this.parseApiDate(event.endDate) : this.parseApiDate(event.startDate);
            let isAllDay = this.isFullDayRange(start, end);
            if (this.selectedView === 'dayGridMonth') {
                isAllDay = true;
            }
            if (isAllDay) {
                end.setDate(end.getDate() + 1);
                end.setHours(0, 0, 0, 0);
            }
            return {
                title: event.eventName,
                start: this.formatLocalIso(start),
                end: this.formatLocalIso(end),
                color: event.colorCode,
                isHoliday: event.isHoliday,
                id: event.pkEventId,
                allDay: isAllDay,
            };
        });
    }
    
    

    // function on click the publish button
    openPublishConfirmBox() {
        this.calendarPublishConfirmBox = true;
        this.editMode = false;
        this.editEventVisible = false;
        this.createEventVisible = false;
        this.deleteEventVisible = false;
        this.eventDetailsVisible = false;
        this.editMode = false
    }

    // Function for publish the calendar
    async publishCalendar() {
        try {
            const body = {
                lang: this.language,
                academicId: this.academicEventCalendarService.academicId || null,
                calendarId: this.academicCalendarId
            }
            this.academicCalendarFormSubmit = true;
            const response: any = await firstValueFrom(this.academicEventCalendarService.publishCalendarService(body));
            this.academicCalendarFormSubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, 'success');
                }
                else {
                    const publishCalendarSuccessTranslation = await firstValueFrom(this.translate.get('publishCalendarSuccess'));
                    customToastr(publishCalendarSuccessTranslation, 'success');
                }
                this.isCalendarPublish = true;
                await this.getEventList(this.academicStartDate, this.academicEndDate);
            }
            this.closeForm();
        }
        catch (error) {
            this.academicCalendarFormSubmit = false;
            const unableToPublishCalendarErrorMessageTranslation = await firstValueFrom(this.translate.get('unableToPublishCalendarErrorMessage'));
            customToastr(error.error.message ? error.error.message : unableToPublishCalendarErrorMessageTranslation, "error");
        }
    }

    // function for open create event form
    createEventForm() {
        this.editEventVisible = false;
        this.createEventVisible = true;
        this.deleteEventVisible = false;
        this.eventDetailsVisible = false;
        this.editMode = false
        this.calendarPublishConfirmBox = false;
        this.editButtonVisible = false;
        this.cancelAddCategoryInput();
        this.eventPopupVisible = true;
    }

    // function for open the edit event form
    editEventForm() {
        this.enableFormControls();
        this.editEventVisible = true;
        this.createEventVisible = false;
        this.deleteEventVisible = false;
        this.eventDetailsVisible = false;
        this.editMode = true;
        this.calendarPublishConfirmBox = false;
    }

    // function for the edit event details
    async eventFormSubmit() {
        try {
            if (this.academicCalendarForm.valid) {
                const selectStaff = this.academicCalendarForm.get('selectedStaffs')?.value;
                const body = {
                    eventId: this.selectedEventId,
                    academicId: this.academicEventCalendarService.academicId,
                    lang: this.language,
                    title: this.academicCalendarForm
                        .get("academicEventName")
                        ?.value?.trim(),
                    description: this.academicCalendarForm
                        .get("academicEventDescription")
                        ?.value?.trim() || null,
                    startDate: this.academicCalendarForm
                        .get("startDate")
                        ?.value?.toString() || null,
                    endDate: this.academicCalendarForm
                        .get("endDate")
                        ?.value?.toString() || null,
                    startTime : this.academicCalendarForm
                    .get("startTime")
                    ?.value?.toString() || null ,
                    endTime: this.academicCalendarForm
                    .get("endTime")?.value?.toString() || null,
                    eventCategoryId: this.academicCalendarForm.get('selectedEventCategory')?.value || null,
                    selectedClasses: this.academicCalendarForm.get('selectedClasses')?.value || null,
                    selectedStaffs: selectStaff.length === this.staffListData.length ? 'All' : selectStaff || null,
                    eventType :this.academicCalendarForm.get('selectedEventType')?.value,
                    eventRepeatId : this.eventRepeatId || null
                }
                this.disableFormControls();
                this.academicCalendarFormSubmit = true;
                const response: any = await firstValueFrom(this.academicEventCalendarService.editEventService(body));
                if (response?.status) {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, 'success');
                    }
                    else {
                        const eventEditSuccessTextTranslation = await firstValueFrom(this.translate.get('eventEditSuccessText'));
                        customToastr(eventEditSuccessTextTranslation, 'success');
                    }
                    const selectedCategory = this.eventCategoryListData.find((category: any) => category.id === body.eventCategoryId);
                    const selectedCategoryColor = this.categoryColorList.find((color: any) => color.pkColorId === selectedCategory?.pkColorId);
                    this.updateLocalEventRecord(this.selectedEventId, {
                        eventName: body.title,
                        description: body.description,
                        startDate: body.startTime ? `${body.startDate}T${body.startTime}:00` : body.startDate,
                        endDate: body.endTime ? `${body.endDate}T${body.endTime}:00` : body.endDate,
                        fkEventCategory: body.eventCategoryId,
                        colorCode: selectedCategoryColor?.colorCode,
                        classId: body.selectedClasses,
                        staffAccessibility: selectStaff,
                        eventType: body.eventType,
                        eventRepeatId: body.eventRepeatId,
                    });
                    this.closeForm();
                }
                this.enableFormControls();
                this.academicCalendarFormSubmit = false;
            }
        }
        catch (error) {
            this.enableFormControls();
            this.academicCalendarFormSubmit = false;
            const unableEditErrorTextTranslation = await firstValueFrom(this.translate.get('unableEditErrorText'));
            customToastr(error.error.message ? error.error.message : unableEditErrorTextTranslation, "error");
        }
    }

    // function for open the delete event confirmation box
    deleteEventForm() {
        this.deleteEventVisible = true;
        this.editEventVisible = false;
        this.createEventVisible = false;
        this.eventDetailsVisible = false;
        this.editMode = false;
        this.calendarPublishConfirmBox = false;
    }

    // function for delete the event 
    async deleteEvents() {
        try {
            const body = {
                "lang": this.language,
                "academicId": this.academicEventCalendarService.academicId,
                "eventId": this.selectedEventId,
                "eventRepeatId" : this.eventRepeatId
            }
            this.academicCalendarFormSubmit = true;
            const response: any = await firstValueFrom(this.academicEventCalendarService.deleteEventService(body));
            this.academicCalendarFormSubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, "success");
                }
                else {
                    const deleteEventSuccessfullTranslation = await firstValueFrom(this.translate.get('deleteEventSuccessfull'));
                    customToastr(deleteEventSuccessfullTranslation, "success");
                }
                if (this.selectedEventType === 'repeat' && this.eventRepeatId) {
                    this.eventDetails = this.eventDetails.filter((item) => item?.eventRepeatId != this.eventRepeatId);
                    this.refreshCalendarViewFromEventDetails();
                }
                else {
                    this.removeLocalEventRecord(this.selectedEventId);
                }
            }
            this.closeForm();
        }
        catch (error) {
            this.academicCalendarFormSubmit = false;
            const deleteAcademicEventErrorTranslation = await firstValueFrom(this.translate.get('deleteAcademicEventError'));
            customToastr(error.error.message ? error.error.message : deleteAcademicEventErrorTranslation, "error");
        }
    }

    // function for clear the staff data
    clearStaffData() {
        this.academicCalendarForm.get('selectedStaffs').setValue([]);
    }

    // function to close all modals
    closeForm() {
        this.enableFormControls();
        this.editEventVisible = this.deleteEventVisible = this.createEventVisible = this.showOverlay = this.eventDetailsVisible = false;
        this.academicCalendarForm.reset(
            {
                academicEventName: "",
                academicEventDescription: "",
                startDate: "",
                endDate: "",
                startTime :'',
                endTime :'',
                selectedClasses: [],
                selectedStaffs: [],
                selectedEventCategory: null,
                selectedEventType :''
            }
        );
        this.academicCalendarForm.get('selectedClasses')?.setValue([]);
        this.academicCalendarForm.get('selectedStaffs')?.setValue([]);
        this.selectedEventType = '';
        this.selectedClassId = [];
        this.selectedStaffId = [];
        this.eventRepeatId = null;
        this.editMode = false;
        this.editButtonVisible = false;
        this.calendarPublishConfirmBox = false;
        this.eventPopupVisible = false;
        this.cancelAddCategoryInput();
    }

    // function for select the multiple date and open create event form
    handleSelect(arg: any) {
        this.createEventForm();
        const startDate = arg.startStr;
        let endDate = new Date(arg.end);
        endDate.setDate(endDate.getDate() - 1);
        const formattedEndDate = endDate.toISOString().split('T')[0];
        this.academicCalendarForm.patchValue({
            startDate: startDate,
            endDate: formattedEndDate,
        });
    }

    // function for the top "+ Create Event" button, opens the create chooser for today
    openCreateChooser() {
        this.handleDayClick(new Date());
    }

    // function for click on an empty day cell, opens the "what do you want to create" chooser
    handleDayClick(date: Date) {
        this.createChooserSelectedDate = date;
        this.createChooserSelection = null;
        this.createChooserPopupVisible = true;
    }

    // function to pick an option (event or holiday) in the create chooser
    selectCreateChooserOption(option: 'event' | 'holiday') {
        this.createChooserSelection = option;
    }

    // function to close the create chooser popup
    closeCreateChooser() {
        this.createChooserPopupVisible = false;
        this.createChooserSelection = null;
        this.createChooserSelectedDate = null;
    }

    // function to format a Date as a local YYYY-MM-DD string, without shifting the day via UTC conversion
    formatDateLocal(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // function to continue from the create chooser into the selected create flow
    continueCreateChooser() {
        if (!this.createChooserSelection) {
            return;
        }
        const selectedDate = this.createChooserSelectedDate
            ? this.formatDateLocal(new Date(this.createChooserSelectedDate))
            : '';
        if (this.createChooserSelection === 'event') {
            this.createEventForm();
            this.academicCalendarForm.patchValue({
                startDate: selectedDate,
                endDate: selectedDate,
            });
        }
        else {
            this.createHolidayForm.reset({
                holidayName: "",
                holidayDescription: "",
                holidayStartDate: selectedDate,
                holidayEndDate: selectedDate,
                holidaySelectedClasses: []
            });
            this.createHolidaySelectedClassId = [];
            this.createHolidayPopupVisible = true;
        }
        this.closeCreateChooser();
    }

    // function to select the classes for the holiday being created
    selectCreateHolidayClasses(selectedIds: any) {
        this.createHolidayForm.markAsDirty();
        this.createHolidaySelectedClassId = [...new Set(selectedIds || [])];
    }

    // function to close the create-holiday popup
    closeCreateHolidayForm() {
        this.createHolidayPopupVisible = false;
        this.createHolidayForm.reset({
            holidayName: "",
            holidayDescription: "",
            holidayStartDate: "",
            holidayEndDate: "",
            holidaySelectedClasses: []
        });
        this.createHolidaySelectedClassId = [];
    }

    // function to submit the new holiday
    async createHolidayFormSubmitFn() {
        try {
            if (this.createHolidayForm.invalid) {
                return;
            }
            const body: any = {
                academicId: this.academicHolidaysService.academicId,
                lang: this.language,
                title: this.createHolidayForm.get('holidayName')?.value?.trim(),
                description: this.createHolidayForm.get('holidayDescription')?.value?.trim(),
                startDate: this.createHolidayForm.get('holidayStartDate')?.value?.toString(),
                endDate: this.createHolidayForm.get('holidayEndDate')?.value?.toString(),
                selectedClasses: this.createHolidaySelectedClassId
            };
            const description = body.description?.trim();
            if (!description) {
                delete body.description;
            }
            this.createHolidayFormSubmit = true;
            const response: any = await firstValueFrom(this.academicHolidaysService.createHolidayService(body));
            this.createHolidayFormSubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, 'success');
                }
                else {
                    const holidayCreatedSuccessfullyTranslation = await firstValueFrom(this.translate.get('holidayCreatedSuccessfully'));
                    customToastr(holidayCreatedSuccessfullyTranslation, 'success');
                }
                this.closeCreateHolidayForm();
                await this.getEventList(this.academicStartDate, this.academicEndDate);
                if (body.startDate) {
                    this.ngfcDate = new Date(body.startDate);
                }
            }
        }
        catch (error) {
            this.createHolidayFormSubmit = false;
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error?.error?.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function for click on the event in the calendar
    handleEventClick(event: CalendarEvent) {
        const isHoliday = event?.meta?.['isHoliday'];
        if (isHoliday) {
            this.handleHolidayClick(event);
            return;
        }
        this.enableFormControls();
        this.eventDetailsVisible = false;
        this.deleteEventVisible = false;
        this.editEventVisible = true;
        this.createEventVisible = false;
        this.editMode = true;
        this.selectedEventId = event?.id;
        this.editButtonVisible = false;
        this.cancelAddCategoryInput();
        this.eventPopupVisible = true;
        const matchingEvent = this.eventDetails.find((item) => item?.pkEventId == event?.id);
        const selectedClassObjects = this.classDropdownDataArray.filter(classItem =>
            matchingEvent.classId.some(eventClassId => eventClassId == classItem.pkClassStandardsId)
        );
        const selectedClassIds = selectedClassObjects.map(classItem => classItem.pkClassStandardsId);
        const staffIdsFromEvent = matchingEvent.staffAccessibility;
        const selectedStaffObjects = this.staffListData.filter(staff => {
            return staffIdsFromEvent.some(element => element == staff.staffId);
        });
        this.selectedEventType = matchingEvent.eventType || '';
        this.eventRepeatId = matchingEvent.eventRepeatId
        const selectedStaffIds = selectedStaffObjects.map(staff => staff.staffId);
        this.academicCalendarForm.patchValue({
            startDate: new Date(matchingEvent.startDate).toISOString().split('T')[0],
            endDate: new Date(matchingEvent.endDate).toISOString().split('T')[0],
            startTime: new Date(matchingEvent.startDate).getUTCHours().toString().padStart(2, '0') + ":" +
               new Date(matchingEvent.startDate).getUTCMinutes().toString().padStart(2, '0'),
            endTime: new Date(matchingEvent.endDate).getUTCHours().toString().padStart(2, '0') + ":" +
             new Date(matchingEvent.endDate).getUTCMinutes().toString().padStart(2, '0'),
            academicEventName: matchingEvent.eventName,
            academicEventDescription: matchingEvent.description,
            selectedClasses: selectedClassIds,
            selectedEventCategory: matchingEvent.fkEventCategory,
            selectedStaffs: selectedStaffIds || null,
            selectedEventType : matchingEvent.eventType || '',
        });
    }

    // function for click on a holiday event in the calendar, opens the edit-holiday popup
    async handleHolidayClick(event: CalendarEvent) {
        const matchingHoliday = this.eventDetails.find((item) => item?.pkEventId == event?.id);
        if (!matchingHoliday) {
            return;
        }
        try {
            const response: any = await firstValueFrom(this.academicHolidaysService.getHolidayListService());
            if (!response?.status) {
                return;
            }
            const holidayList = response?.body?.holidayList || [];
            const holidayStartDate = new Date(matchingHoliday.startDate).toISOString().split('T')[0];
            const matchingHolidayRecord = holidayList.find((holiday: any) =>
                holiday.holidayTitle === matchingHoliday.eventName &&
                holiday.StartDate?.split('T')[0] === holidayStartDate
            );
            if (!matchingHolidayRecord) {
                const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
                customToastr(getApiErrorMessageTranslation, "error");
                return;
            }
            this.selectedHolidayEventId = matchingHolidayRecord.pkSchoolHolidaysId;
            this.selectedHolidayCalendarEventId = matchingHoliday.pkEventId;
            const selectedClassObjects = this.classDropdownDataArray.filter(classItem =>
                matchingHoliday.classId.some(holidayClassId => holidayClassId == classItem.pkClassStandardsId)
            );
            this.selectedHolidayClassId = selectedClassObjects.map(classItem => classItem.pkClassStandardsId);
            this.editHolidayForm.patchValue({
                holidayName: matchingHoliday.eventName,
                holidayDescription: matchingHoliday.description,
                holidayStartDate: holidayStartDate,
                holidayEndDate: new Date(matchingHoliday.endDate).toISOString().split('T')[0],
                holidaySelectedClasses: this.selectedHolidayClassId,
            });
            this.editHolidayForm.markAsPristine();
            this.editHolidayPopupVisible = true;
        }
        catch (error) {
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error?.error?.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function to select the classes for the holiday being edited
    selectHolidayClasses(selectedIds: any) {
        this.editHolidayForm.markAsDirty();
        this.selectedHolidayClassId = [...new Set(selectedIds || [])];
    }

    // function to close the edit-holiday popup
    closeEditHolidayForm() {
        this.editHolidayPopupVisible = false;
        this.editHolidayForm.reset({
            holidayName: "",
            holidayDescription: "",
            holidayStartDate: "",
            holidayEndDate: "",
            holidaySelectedClasses: []
        });
        this.selectedHolidayEventId = '';
        this.selectedHolidayCalendarEventId = '';
        this.selectedHolidayClassId = [];
    }

    // function to submit the edited holiday
    async editHolidayFormSubmitFn() {
        try {
            if (this.editHolidayForm.invalid) {
                return;
            }
            const body: any = {
                academicId: this.academicHolidaysService.academicId,
                holidayId: this.selectedHolidayEventId,
                lang: this.language,
                title: this.editHolidayForm.get('holidayName')?.value?.trim(),
                description: this.editHolidayForm.get('holidayDescription')?.value?.trim(),
                startDate: this.editHolidayForm.get('holidayStartDate')?.value?.toString(),
                endDate: this.editHolidayForm.get('holidayEndDate')?.value?.toString(),
                selectedClasses: this.selectedHolidayClassId
            };
            const description = body.description?.trim();
            if (!description) {
                delete body.description;
            }
            this.editHolidayFormSubmit = true;
            const response: any = await firstValueFrom(this.academicHolidaysService.editHolidayService(body));
            this.editHolidayFormSubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, 'success');
                }
                else {
                    const holidayUpdatedSuccessfullyTranslation = await firstValueFrom(this.translate.get('holidayUpdatedSuccessfully'));
                    customToastr(holidayUpdatedSuccessfullyTranslation, 'success');
                }
                this.updateLocalEventRecord(this.selectedHolidayCalendarEventId, {
                    eventName: body.title,
                    description: body.description || null,
                    startDate: body.startDate,
                    endDate: body.endDate,
                    classId: body.selectedClasses,
                });
                this.closeEditHolidayForm();
            }
        }
        catch (error) {
            this.editHolidayFormSubmit = false;
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error?.error?.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function to open the delete-confirmation popup for a holiday
    openDeleteHolidayConfirm() {
        this.deleteHolidayConfirmVisible = true;
    }

    // function to close the delete-confirmation popup for a holiday
    closeDeleteHolidayConfirm() {
        this.deleteHolidayConfirmVisible = false;
    }

    // function to delete the holiday being edited
    async deleteHolidayFn() {
        try {
            const body = {
                lang: this.language,
                academicId: this.academicHolidaysService.academicId,
                holidayId: this.selectedHolidayEventId,
            };
            this.deletingHolidaySubmit = true;
            const response: any = await firstValueFrom(this.academicHolidaysService.deleteHolidayService(body));
            this.deletingHolidaySubmit = false;
            if (response?.status) {
                if (response?.body?.message) {
                    customToastr(response?.body?.message, "success");
                }
                else {
                    const deleteHolidaySuccessfullTranslation = await firstValueFrom(this.translate.get('deleteHolidaySuccessfull'));
                    customToastr(deleteHolidaySuccessfullTranslation, "success");
                }
                this.removeLocalEventRecord(this.selectedHolidayCalendarEventId);
                this.closeDeleteHolidayConfirm();
                this.closeEditHolidayForm();
            }
        }
        catch (error) {
            this.deletingHolidaySubmit = false;
            const getApiErrorMessageTranslation = await firstValueFrom(this.translate.get('getApiErrorMessage'));
            customToastr(error?.error?.message ? error.error.message : getApiErrorMessageTranslation, "error");
        }
    }

    // function for click on an event in ng-full-calendar, opens the custom event details form
   

    
    // function for change the month in the academic calendar
    // async handleDatesSet(arg: any) {
    //     const startDate = new Date(arg.start).toISOString();
    //     const endDate = new Date(arg.end).toISOString();
    //     if (this.currentStartDate === startDate && this.currentEndDate === endDate) return;
    //     this.currentStartDate = startDate;
    //     this.currentEndDate = endDate;
    //     await this.getEventList();
    // }

    // fuction to limits the rate at which a getEventList function is executed
    // rateLimit() {
    //     if (this.isThrottled) return;
    //     this.isThrottled = true;    
    //     setTimeout(async () => {
    //         await this.getEventList();
    //         this.isThrottled = false; 
    //     }, 200);
    // }

    // function on click of the submit button of the create and edit event form
    onFormSubmit() {
        this.editEventVisible ? this.eventFormSubmit() : this.createEventFormSubmit();
    }

    // function for create the event 
    async createEventFormSubmit() {
        try {
            if (this.academicCalendarForm.valid) {
                const selectStaff = this.academicCalendarForm.get('selectedStaffs')?.value;
                const body = {
                    academicId: this.academicEventCalendarService.academicId,
                    lang: this.language,
                    title: this.academicCalendarForm
                        .get("academicEventName")
                        ?.value?.trim(),
                    description: this.academicCalendarForm
                        .get("academicEventDescription")
                        ?.value?.trim(),
                    startDate: this.academicCalendarForm
                        .get("startDate")
                        ?.value?.toString(),
                    endDate: this.academicCalendarForm
                        .get("endDate")
                        ?.value?.toString(),
                    eventCategoryId: this.academicCalendarForm.get('selectedEventCategory')?.value,
                    selectedClasses: this.academicCalendarForm.get('selectedClasses')?.value,
                    selectedStaffs: selectStaff.length === this.staffListData.length ? 'All' : selectStaff,
                    startTime : this.academicCalendarForm
                    .get("startTime")
                    ?.value?.toString(),
                    endTime: this.academicCalendarForm
                    .get("endTime")?.value?.toString(),
                    eventType :this.academicCalendarForm.get('selectedEventType')?.value
                }
                const description = body.description?.trim();
                if (!description) {
                    delete body.description;
                }
                this.disableFormControls();
                this.academicCalendarFormSubmit = true;
                const response: any = await firstValueFrom(this.academicEventCalendarService.createEventService(body));
                if (response?.status) {
                    if (response?.body?.message) {
                        customToastr(response?.body?.message, 'success');
                    }
                    else {
                        const eventCreateSuccessTextTranslation = await firstValueFrom(this.translate.get('eventCreateSuccessText'));
                        customToastr(eventCreateSuccessTextTranslation, 'success');
                    }
                    this.closeForm();
                    await this.getEventList(this.academicStartDate, this.academicEndDate);
                }
                this.enableFormControls();
                this.academicCalendarFormSubmit = false;
            }
        }
        catch (error) {
            this.enableFormControls();
            this.academicCalendarFormSubmit = false;
            const unableCreateErrorTextTranslation = await firstValueFrom(this.translate.get('unableCreateErrorText'));
            customToastr(error?.error?.message? error?.error?.message : unableCreateErrorTextTranslation, "error")
        }
    }

    // function for disable the input form fields
    disableFormControls() {
        Object.keys(this.academicCalendarForm.controls).forEach((controlName) => {
            this.academicCalendarForm.get(controlName)?.disable();
        });
    }

    // function for enable the input form fields
    enableFormControls() {
        Object.keys(this.academicCalendarForm.controls).forEach((controlName) => {
            this.academicCalendarForm.get(controlName)?.enable();
        });
    }

    // function for style the holiday tile in the calendar 
    styleHolidayTiles(arg: any): string[] {
        const localDateStr = new Date(arg.date).toISOString().split('T')[0];
        if (this.holidayDates.includes(localDateStr)) {
            return ['holiday-tile'];
        }
        return [];
    }
    updateView(view: string) {
        this.selectedView = view;
        if (this.calendarComponent) {
            const calendarApi = this.calendarComponent.getApi();
            calendarApi.changeView(view);
          }
      }

      handleEventTypeChange(event) {
        this.academicCalendarForm.markAsDirty();
        this.selectedEventType = event.trim(); 
    }
    

    // function for initialization of the calendar
    // loadCalendar() {
    //     this.calendarOptions = {
    //         initialView:this.selectedView,
    //          plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin],
    //         selectable: true,
    //         events: this.calendarEvents,
    //         editable: false,
    //         eventColor: '',
    //         validRange: this.validDateRange,
    //         timeZone: 'UTC',
    //         dayCellClassNames: this.styleHolidayTiles.bind(this),             
    //         select: (arg) => this.handleSelect(arg),
    //         eventClick: (arg) => this.handleEventClick(arg),
    //         datesSet: (arg) => this.handleDatesSet(arg),
    //     };
    // }
}